import * as React from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import Box from '@components/layout/box';
import Upload from '@webapp/components/upload';
//import HintArrow from '@components/basics/hint-arrow';
import { ButtonType } from '@components/actions/button';
import CoverArtIcon from '@components/basics/icons/cover-art';
import UploadItem from '@webapp/components/upload/upload-item';

import { CreateDialog } from '@webapp/store/ui/action-creators';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';

import { upload } from '@webapp/sdk/assets';
import { AssetUploadResponse } from '@webapp/sdk/assets';

import { promiseDelay } from '@webapp/utils/promise-delay';

import { AssetFile } from '@common/types/data/asset-file';
import { ImageData } from '@webapp/types/common/image-data';
import { DialogConfiguration } from '@webapp/types/ui/dialog-configuration';

interface UploadArtworkCtrlProps {
  id?: string;
  className?: string;
  imgUrl?: string;
  hasError?: React.ReactNode;
  onError: (message: string) => void;
  onChange: (asset?: AssetFile) => void;
  openModal: (
    artworkId: string,
    callback: (value: any) => void
  ) => CreateDialog;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const buildDialog = (
  callback: (value: any) => void
): DialogConfiguration<ButtonType> => {
  return {
    title: <Translate id="UPLOAD.COVER.DELETE.MODAL.TITLE" />,
    text: <Translate id="UPLOAD.COVER.DELETE.MODAL.TEXT" />,
    actions: [
      { type: 'primary', label: 'No', value: false },
      { type: 'secondary', label: 'Yes', value: true },
    ],
    callback,
  };
};

const readImage = (f: File): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && e.target.result) {
        const img = new Image();
        const src = e.target.result.toString();

        img.onload = () => {
          resolve({
            src,
            width: img.width,
            height: img.height,
            type: f.type,
          });
        };

        img.onerror = () => reject(new Error('Error while loading file'));
        img.src = src;
      }
    };
    reader.readAsDataURL(f);
  });
};

const validateImageFormat = (file: File) => {
  if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
    throw new Error('RELEASE.INVALID_FORMAT');
  }
};

const validateImageSize = (image: ImageData) => {
  if (
    image.width < 3000 ||
    image.height < 3000 ||
    image.width > 6000 ||
    image.height > 6000 ||
    image.width !== image.height
  ) {
    throw new Error('RELEASE.INVALID_SIZE');
  }
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openModal: (artworkId: string, callback: (value: any) => void) =>
    dispatch(new CreateDialog(artworkId, buildDialog(callback))),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(field, value, error, releaseId)),
});

export const UploadArtworkCtrl: React.FC<UploadArtworkCtrlProps> = (props) => {
  const [progress, setProgress] = React.useState(0);
  const [status, setStatus] = React.useState<'uploading' | 'done'>();
  const [imgUrl, setImgUrl] = React.useState(props.imgUrl || '');
  const [error, setError] = React.useState('');
  const [errorImgFormat, setErrorImgFormat] = React.useState(false);
  const [errorImgSize, setErrorImgSize] = React.useState(false);
  const [request, setRequest] = React.useState<AssetUploadResponse>();

  const propsUrl = props.imgUrl;

  React.useEffect(() => {
    if (propsUrl) {
      setImgUrl(propsUrl);
    }
  }, [propsUrl]);

  const handleFile = async (files: FileList | File[]) => {
    const file = files[0];
    if (file.size > 10485760) {
      setErrorImgSize(true);
      props.onError('RELEASE.INVALID_FORMAT');
      return;
    }

    setError('');
    setErrorImgFormat(false);
    setErrorImgSize(false);
    props.onError('');

    try {
      validateImageFormat(file);

      const image = await readImage(file);

      validateImageSize(image);

      setStatus('uploading');
      setProgress(0);
      setImgUrl(image.src);

      const newRequest = await upload('artwork', file);

      setRequest(newRequest);

      newRequest.upload.progress.subscribe((p) => setProgress(p));
      await newRequest.upload.request;

      await promiseDelay(1500);

      setStatus('done');
      props.onChange({
        url: newRequest.url,
        id: newRequest.id,
        contentType: file.type,
        size: file.size,
        crc32: '',
        key: newRequest.key,
        created_at: new Date(),
        updated_at: new Date(),
      });
    } catch (e) {
      const error = e as Error;

      switch (error.message) {
        case 'RELEASE.INVALID_SIZE': {
          setErrorImgSize(true);
          break;
        }
        case 'RELEASE.INVALID_FORMAT': {
          setErrorImgFormat(true);
          break;
        }
        default: {
          setError(error ? error.message : 'RELEASE.UNKNOWN_ERROR');
          break;
        }
      }
      props.sendAnalytics(
        'Release Cover Art',
        '',
        error.message ? error.message : 'RELEASE.UNKNOWN_ERROR'
      );
      props.onError(error ? error.message : 'RELEASE.UNKNOWN_ERROR');
    }
  };

  const onCancel = () => {
    if (request) {
      request.upload.cancel();
    }

    setStatus(undefined);
    setProgress(0);
    setImgUrl('');
  };

  const getDragText = () => {
    switch (status) {
      case 'uploading':
        return <Translate id="UPLOAD.COVER.DRAG.REPLACE" />;
      case 'done':
        return <Translate id="UPLOAD.COVER.DRAG.REPLACE" />;
      default:
        return <Translate id="UPLOAD.COVER.DRAG.DEFAULT" />;
    }
  };

  // TODO: DEVTEAM-958 - Integrate goToCreateArtwork - Send the user to the Artwork creation screen.
  //const goToCreateArtwork = () => {
  //return;
  //};

  return (
    <Box noBorderTop noBorderBottom className={props.className}>
      <Upload
        status={status}
        progress={progress}
        error={error || errorImgSize || errorImgFormat}
        onChange={handleFile}
        noProgressline
      >
        <UploadItem
          id={props.id}
          title={<Translate id="UPLOAD.COVER.TITLE" />}
          dragText={getDragText()}
          label={
            <>
              <span className={errorImgSize ? 'error' : ''}>
                <Translate id="UPLOAD.COVER.SIZE" />
              </span>{' '}
              <span className={errorImgFormat ? 'error' : ''}>
                <Translate id="UPLOAD.COVER.DETAILS" />
              </span>
            </>
          }
          buttonType="secondary-dark"
          accept="image/*"
          status={status}
          error={error || errorImgSize || errorImgFormat}
          // btnDisabled={status === 'uploading'}
          isMultiline
          icon={<CoverArtIcon />}
          imgUrl={imgUrl}
          onChange={(files: FileList) => handleFile(files)}
          onCancel={onCancel}
          progress={progress}
        />
      </Upload>

      {props.hasError && props.hasError}
    </Box>
  );
};

export default connect(
  null,
  mapDispatchToProps
)(styled(UploadArtworkCtrl)`
  .artwork {
    flex-shrink: 0;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
  }
`);
